import { Head, Link, router } from "@inertiajs/react"
import { FileText, Clock, CheckCircle, AlertCircle, Search, Mic } from "lucide-react"
import { useState } from "react"
import { AdminLayout } from "../components/AdminLayout"
import { SectionTabs } from "../components/SectionTabs"

const articlesTabs = [
  { label: "All Articles", href: "/app/admin/articles" },
  { label: "Pending Review", href: "/app/admin/articles/pending_review" },
  { label: "Meetings", href: "/app/admin/meetings" },
]
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { PodcastStatusBadge } from "@/components/podcast/PodcastStatusBadge"
import ReassignedFrom from "@/components/admin/ReassignedFrom"

const statusConfig = {
  draft: { label: "Draft", variant: "secondary", icon: FileText },
  pending_review: { label: "Pending Review", variant: "warning", icon: Clock },
  published: { label: "Published", variant: "success", icon: CheckCircle },
  rejected: { label: "Rejected", variant: "destructive", icon: AlertCircle },
}

function StatusBadge({ status }) {
  const config = statusConfig[status] || statusConfig.draft
  return (
    <Badge
      variant={config.variant}
      className={
        config.variant === "warning"
          ? "bg-amber-500 text-white hover:bg-amber-500"
          : config.variant === "success"
          ? "bg-green-500 text-white hover:bg-green-500"
          : ""
      }
    >
      {config.label}
    </Badge>
  )
}

function formatDate(dateString) {
  if (!dateString) return "-"
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  })
}

const statusFilters = [
  { key: null, label: "All", statKey: "total" },
  { key: "draft", label: "Drafts", statKey: "drafts" },
  { key: "pending_review", label: "Pending", statKey: "pendingReview" },
  { key: "published", label: "Published", statKey: "published" },
  { key: "rejected", label: "Rejected", statKey: "rejected" },
]

export default function ArticlesIndex({
  currentUser,
  articles,
  stats,
  categories,
  coaches,
  currentStatus,
  currentAuthorId,
  currentQuery,
  pagination,
}) {
  const [searchQuery, setSearchQuery] = useState(currentQuery || "")

  // Build params from current filters, preserving all active filters
  const buildParams = (overrides = {}) => {
    const params = {}
    const status = "status" in overrides ? overrides.status : currentStatus
    const authorId = "author_id" in overrides ? overrides.author_id : currentAuthorId
    const q = "q" in overrides ? overrides.q : currentQuery
    const page = "page" in overrides ? overrides.page : undefined

    if (status) params.status = status
    if (authorId) params.author_id = authorId
    if (q) params.q = q
    if (page && page > 1) params.page = page
    return params
  }

  const handleFilterChange = (status) => {
    router.get("/app/admin/articles", buildParams({ status: status || undefined, page: 1 }), { preserveState: true })
  }

  const handleCoachFilterChange = (authorId) => {
    const value = authorId === "all" ? undefined : authorId
    router.get("/app/admin/articles", buildParams({ author_id: value, page: 1 }), { preserveState: true })
  }

  const handleSearchSubmit = (e) => {
    e.preventDefault()
    router.get("/app/admin/articles", buildParams({ q: searchQuery || undefined, page: 1 }), { preserveState: true })
  }

  // Generate windowed page numbers (show ~5 at a time)
  const getPageNumbers = () => {
    if (!pagination) return []
    const { currentPage, totalPages } = pagination
    const windowSize = 5
    let start = Math.max(1, currentPage - Math.floor(windowSize / 2))
    let end = Math.min(totalPages, start + windowSize - 1)
    if (end - start < windowSize - 1) {
      start = Math.max(1, end - windowSize + 1)
    }
    const pages = []
    for (let i = start; i <= end; i++) pages.push(i)
    return pages
  }



  return (
    <AdminLayout currentUser={currentUser} pendingCount={stats?.pendingReview}>
      <Head title="All Articles - Kingdom Factor Admin" />

      <div className="space-y-6">
        <SectionTabs tabs={articlesTabs} />

        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">All Articles</h1>
          <p className="text-zinc-500">Manage all article submissions</p>
        </div>

        {/* Stats */}
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
          {statusFilters.map((filter) => {
            const count = stats?.[filter.statKey] || 0
            const isActive = currentStatus === filter.key
            return (
              <button
                key={filter.label}
                onClick={() => handleFilterChange(filter.key)}
                className={`p-4 rounded-lg border text-left transition-colors ${
                  isActive
                    ? "border-kf-red bg-red-50"
                    : "border-zinc-200 hover:border-zinc-300 bg-white"
                }`}
              >
                <p className="text-sm text-zinc-500">{filter.label}</p>
                <p className="text-2xl font-bold text-zinc-900">{count}</p>
              </button>
            )
          })}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Coach filter */}
          <Select
            value={currentAuthorId || "all"}
            onValueChange={handleCoachFilterChange}
          >
            <SelectTrigger className="w-full sm:w-[200px] bg-white border-zinc-300 text-zinc-900">
              <SelectValue placeholder="All Coaches" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Coaches</SelectItem>
              {coaches?.map((coach) => (
                <SelectItem key={coach.id} value={String(coach.id)}>
                  {coach.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Search */}
          <form onSubmit={handleSearchSubmit} className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
            <Input
              type="search"
              placeholder="Search by title or interviewee..."
              className="pl-9 bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchSubmit(e)
              }}
            />
          </form>
        </div>

        {/* Articles Table */}
        <Card className="bg-white border-zinc-200">
          <CardContent className="p-0">
            {articles?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-200 hover:bg-transparent">
                    <TableHead className="text-zinc-500">Title</TableHead>
                    <TableHead className="text-zinc-500 hidden lg:table-cell">Author</TableHead>
                    <TableHead className="text-zinc-500 hidden lg:table-cell">Category</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Status</TableHead>
                    <TableHead className="text-zinc-500 hidden md:table-cell">Podcast</TableHead>
                    <TableHead className="text-zinc-500 hidden sm:table-cell">Updated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {articles.map((article) => (
                    <TableRow
                      key={article.id}
                      className="cursor-pointer border-zinc-100 hover:bg-zinc-50"
                      onClick={() =>
                        router.visit(`/app/admin/articles/${article.id}`)
                      }
                    >
                      <TableCell className="font-medium text-zinc-900 whitespace-normal">
                        <div className="min-w-0">
                          <p className="line-clamp-2">{article.title}</p>
                          {/* lg:hidden to match the author line below it — on
                              desktop the author cell carries this instead. */}
                          <ReassignedFrom
                            originalAuthor={article.originalAuthor}
                            className="lg:hidden"
                          />
                          <p className="text-xs text-zinc-500 truncate lg:hidden mt-0.5">
                            {article.author?.name || article.author?.email}
                            {article.category ? ` • ${article.category}` : ""}
                          </p>
                          <div className="mt-1.5 flex items-center gap-2 sm:hidden">
                            <StatusBadge status={article.status} />
                            <span className="text-xs text-zinc-500">
                              {formatDate(article.updatedAt)}
                            </span>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden lg:table-cell">
                        {article.author?.name || article.author?.email}
                        <ReassignedFrom
                          originalAuthor={article.originalAuthor}
                          as="span"
                          className="block"
                        />
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden lg:table-cell">
                        {article.category || "-"}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <StatusBadge status={article.status} />
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        {article.podcastEnabled ? (
                          article.podcastStatus ? (
                            <PodcastStatusBadge status={article.podcastStatus} />
                          ) : (
                            <Badge variant="outline" className="border-0 bg-purple-100 text-purple-700">
                              <Mic className="h-3 w-3 mr-1" />
                              Enabled
                            </Badge>
                          )
                        ) : (
                          <span className="text-zinc-300">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-500 hidden sm:table-cell">
                        {formatDate(article.updatedAt)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12 text-zinc-400">
                <FileText className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-500">No articles found</p>
                <p className="text-sm">
                  {currentQuery || currentAuthorId
                    ? "Try adjusting your filters or search term"
                    : "No articles match the current filter"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (() => {
          const pages = getPageNumbers()
          const firstPage = pages[0]
          const lastPage = pages[pages.length - 1]
          const goToPage = (page) => router.get("/app/admin/articles", buildParams({ page }), { preserveState: true })

          return (
            <div className="flex flex-col items-center gap-3">
              <p className="text-sm text-zinc-500">
                Showing {pagination.from} – {pagination.to} of {pagination.totalCount} articles
              </p>
              <Pagination>
                <PaginationContent>
                  {pagination.currentPage > 1 && (
                    <PaginationItem>
                      <PaginationPrevious onClick={() => goToPage(pagination.currentPage - 1)} />
                    </PaginationItem>
                  )}
                  {firstPage > 1 && (
                    <>
                      <PaginationItem>
                        <PaginationLink onClick={() => goToPage(1)}>1</PaginationLink>
                      </PaginationItem>
                      {firstPage > 2 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                    </>
                  )}
                  {pages.map((page) => (
                    <PaginationItem key={page}>
                      <PaginationLink
                        onClick={() => goToPage(page)}
                        isActive={page === pagination.currentPage}
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  ))}
                  {lastPage < pagination.totalPages && (
                    <>
                      {lastPage < pagination.totalPages - 1 && (
                        <PaginationItem>
                          <PaginationEllipsis />
                        </PaginationItem>
                      )}
                      <PaginationItem>
                        <PaginationLink onClick={() => goToPage(pagination.totalPages)}>
                          {pagination.totalPages}
                        </PaginationLink>
                      </PaginationItem>
                    </>
                  )}
                  {pagination.currentPage < pagination.totalPages && (
                    <PaginationItem>
                      <PaginationNext onClick={() => goToPage(pagination.currentPage + 1)} />
                    </PaginationItem>
                  )}
                </PaginationContent>
              </Pagination>
            </div>
          )
        })()}
      </div>
    </AdminLayout>
  )
}
